import React, { useState, useRef, useEffect, useMemo } from "react";
import Select from "react-select";
import DeepFilterPanel, { PhaseSelect, STATUS_OPTIONS, DATE_PRESETS, selectStyles } from "./DeepFilterPanel";
import { EMPTY_FILTERS } from "../../utils/filters";

const FilterChip = ({ label, icon = "ri-filter-line", color = "blue", onRemove }) => {
  const colors = { blue: { bg: "#dce8ff", color: "#002045" }, green: { bg: "#e8f5e9", color: "#1b5e20" }, purple: { bg: "#f3e8ff", color: "#5b21b6" } };
  const c = colors[color] || colors.blue;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: c.bg, color: c.color, fontSize: "0.7rem", fontWeight: 600, padding: "3px 8px 3px 6px", borderRadius: 20 }}>
      <i className={icon} style={{ fontSize: "0.65rem" }} />
      {label}
      <button type="button" onClick={onRemove} style={{ background: "none", border: "none", padding: 0, color: c.color, cursor: "pointer", lineHeight: 1, marginLeft: 2, display: "flex", alignItems: "center" }}>
        <i className="ri-close-line" style={{ fontSize: "0.7rem" }} />
      </button>
    </span>
  );
};

const costsSelectStyles = {
  ...selectStyles,
  control: (base, state) => ({ ...selectStyles.control(base, state), minHeight: 32, fontSize: "0.82rem" }),
};

const FilterBar = ({ count, filters, phases, costTypes, onChange, onAdd }) => {
  const [panelOpen,   setPanelOpen]   = useState(false);
  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    setLocalSearch(filters.search || "");
  }, [filters.search]);

  const handleSearch = (value) => {
    setLocalSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onChange({ ...filters, search: value }), 400);
  };

  const costTypeOptions = useMemo(() => (costTypes || []).map((ct) => ({ value: ct.id, label: ct.name })), [costTypes]);
  const selectedCostTypes = costTypeOptions.filter((o) => (filters.costTypeIds || []).includes(o.value));

  const hiddenActiveCount = [
    filters.statuses?.length > 0,
    !!filters.datePreset,
    !!(filters.amountMin || filters.amountMax),
  ].filter(Boolean).length;

  const hasAnyFilter = filters.search || filters.costTypeIds?.length > 0 || filters.phaseId || hiddenActiveCount > 0;

  return (
    <>
      <div className="bg-white border-start border-end border-top rounded-top-3 d-flex align-items-center px-4 py-3" style={{ background: "#f8f9fb" }}>
        {/* Count */}
        <p className="small fw-bold mb-0 me-3" style={{ color: "#002045", whiteSpace: "nowrap", flexShrink: 0 }}>
          {count} {count === 1 ? "invoice" : "invoices"}
        </p>

        {/* Filters row */}
        <div className="d-flex align-items-center justify-content-center gap-2" style={{ flex: 1, minWidth: 0, padding: "0 16px" }}>
          <div style={{ position: "relative", width: 260 }}>
            <i className="ri-search-line" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "0.9rem", zIndex: 1 }} />
            <input type="text" className="form-control form-control-sm" placeholder="Search vendor, invoice #..."
              value={localSearch} onChange={(e) => handleSearch(e.target.value)}
              style={{ paddingLeft: 30, fontSize: "0.82rem", border: "1px solid #e5e7eb", borderRadius: 7 }} />
          </div>

          {costTypes?.length > 0 && (
            <div style={{ width: 200 }}>
              <Select isMulti options={costTypeOptions} value={selectedCostTypes} placeholder="Cost type..."
                onChange={(sel) => onChange({ ...filters, costTypeIds: (sel || []).map((o) => o.value) })}
                styles={costsSelectStyles} classNamePrefix="rs" />
            </div>
          )}

          {phases?.length > 0 && (
            <div style={{ width: 200 }}>
              <PhaseSelect phases={phases} value={filters.phaseId}
                onChange={(phaseId) => onChange({ ...filters, phaseId })} />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
          <button type="button" onClick={() => setPanelOpen((o) => !o)} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 7,
            border: `1.5px solid ${panelOpen || hiddenActiveCount > 0 ? "#002045" : "#e2e5eb"}`,
            background: panelOpen || hiddenActiveCount > 0 ? "#002045" : "white",
            color: panelOpen || hiddenActiveCount > 0 ? "white" : "#374151",
            fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
          }}>
            <i className={panelOpen ? "ri-close-line" : "ri-equalizer-2-line"} style={{ fontSize: "0.9rem" }} />
            {panelOpen ? "Close" : "Filters"}
            {!panelOpen && hiddenActiveCount > 0 && (
              <span style={{ background: "white", color: "#002045", borderRadius: 20, fontSize: "0.65rem", fontWeight: 700, padding: "0px 6px", lineHeight: "16px" }}>
                {hiddenActiveCount}
              </span>
            )}
          </button>

          {hasAnyFilter && !panelOpen && (
            <button type="button" onClick={() => onChange(EMPTY_FILTERS)} style={{ background: "none", border: "1px solid #e2e5eb", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: "0.72rem", fontWeight: 600, color: "#74777f", display: "flex", alignItems: "center", gap: 4 }}>
              <i className="ri-filter-off-line" /> Clear
            </button>
          )}

          {onAdd && (
            <button type="button" className="btn btn-sm fw-bold px-3"
              style={{ background: "#002045", color: "white", border: "none", borderRadius: 6 }}
              onClick={onAdd}>
              <i className="ri-add-line me-1" />Add Invoice
            </button>
          )}
        </div>
      </div>

      {panelOpen && <DeepFilterPanel filters={filters} onChange={onChange} />}

      {!panelOpen && hiddenActiveCount > 0 && (
        <div className="d-flex flex-wrap gap-2 px-4 py-2 border-start border-end bg-white" style={{ background: "#fafbff" }}>
          {filters.statuses?.length > 0 && (
            <FilterChip label={`Status: ${filters.statuses.map((s) => STATUS_OPTIONS.find((o) => o.value === s)?.label || s).join(", ")}`}
              onRemove={() => onChange({ ...filters, statuses: [] })} />
          )}
          {filters.datePreset && (
            <FilterChip icon="ri-calendar-line" color="green"
              label={DATE_PRESETS.find((p) => p.value === filters.datePreset)?.label || filters.datePreset}
              onRemove={() => onChange({ ...filters, datePreset: "", dateFrom: "", dateTo: "" })} />
          )}
          {(filters.amountMin || filters.amountMax) && (
            <FilterChip icon="ri-money-euro-circle-line" color="purple"
              label={`Amount: ${filters.amountMin || "0"} – ${filters.amountMax || "∞"}`}
              onRemove={() => onChange({ ...filters, amountMin: "", amountMax: "" })} />
          )}
        </div>
      )}
    </>
  );
};

export default FilterBar;
